import type { FC } from 'react';
import SwitcherContainer from '@/containers/SwitcherContainer';
import { ContentWrapper, Body } from './styled';
import loadable from '@loadable/component';
import Media from 'react-media';
import type { Theme } from '@simplywallst/ui-core';
import { LayoutProvider } from '@components/Layout/context/LayoutContext';
import { Provider as SearchProvider } from '@/hooks/useSearch';
import { useTheme } from '@simplywallst/ui-core';
import { useAppInit } from './hooks/useAppInit';
import { useMobileDetect } from '@/hooks/useMobileDetect';
import { BrazeProvider } from '@components/Braze';
import { useEffect } from 'react';
import { useRewardful } from '@/hooks/useRewardful';
import { useUser } from '@/hooks/useUser';
import { useSelector } from 'react-redux';
import { getUi } from '@ducks/ui/selectors';

const SnackBarContainer = loadable(
  () => import('@/components/SnackBar/containers/SnackBarContainer'),
  { ssr: false }
);

const Onboarding = loadable(() => import('@components/Onboarding'), {
  ssr: false,
});

const LoadingOverlay = loadable(() => import('@components/LoadingOverlay'), {
  ssr: false,
});

const MobileFooterMenuContainer = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '@components/MobileFooterMenu/containers/MobileFooterMenuContainer'
    )
);
const TrialEndedPlansModal = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '@components/OurPlans/components/TrialEndedPlansModal/TrialEndedPlansModal'
    )
);
const CheckoutReturnModal = loadable(
  () => import('@components/OurPlans/components/CheckoutReturnModal')
);

export interface WithThemeProps {
  theme: Theme;
}

export interface Props {
  currentPortfolioName: null | string;
  currentUrl: string;
  hasSnackbarNotification: boolean;
  isAuthenticated: boolean;
  hasLimitedCompanyReports: boolean;
  isTrialing: boolean;
  layout: Record<string, any>;
  isRegistrationFromNews: boolean;
}

const Content: FC<Props> = ({
  hasSnackbarNotification,
  hasLimitedCompanyReports,
  isAuthenticated,
  isTrialing,
  layout,
  isRegistrationFromNews,
}) => {
  const ui = useSelector(getUi);
  const { rewardful } = useRewardful();
  const { data: user } = useUser();
  const theme = useTheme();

  const isLoading = ui.loading;

  const showOnboarding =
    (ui.showOnboarding && user.loggedIn && RUNTIME_ENV !== 'server') ||
    isRegistrationFromNews;

  useAppInit();
  // opt-out mobile topbar condition, if not supplied then it will supply the topbar
  // otherwise use the boolean provided for conditionally set instances
  const hasMobileTopbar =
    layout?.mobileTopbar === undefined || !!layout?.mobileTopbar;
  const isMobile = useMobileDetect();

  useEffect(() => {
    // Expresses a state that the user has just registered and User data is available
    if (showOnboarding && user.emailAddress) {
      try {
        if (
          typeof rewardful !== 'undefined' &&
          typeof user.emailAddress !== 'undefined'
        ) {
          rewardful('convert', {
            email: user.emailAddress,
          });
        }
      } catch (err) {}
    }
  }, [showOnboarding, user.emailAddress]);

  return (
    <ContentWrapper
      hasDesktopTopbar={
        layout &&
        layout.desktopTopbar === undefined &&
        layout.desktopTopbar !== false
      }
      hasMobileTopbar={hasMobileTopbar}
      hasMobileTopbarSubNav={layout && layout.mobileTopbarSubNav}
      hasAnnouncement={hasLimitedCompanyReports || isTrialing}
    >
      <BrazeProvider>
        <LayoutProvider layout={layout}>
          <SearchProvider value={null}>
            <Body
              justifyContent="center"
              hasMobileFooterMenu={layout && layout.mobileFooterMenu !== false}
            >
              <SwitcherContainer />
            </Body>
            {layout && layout.mobileFooterMenu !== false && (
              <Media
                query={{ maxWidth: theme.mdMaxRaw }}
                defaultMatches={isMobile}
                render={() => <MobileFooterMenuContainer />}
              />
            )}
            {layout && (
              <>
                {hasSnackbarNotification && <SnackBarContainer />}
                {isLoading && <LoadingOverlay />}
                <TrialEndedPlansModal />
                {isAuthenticated && <CheckoutReturnModal />}
                {showOnboarding && <Onboarding />}
              </>
            )}
          </SearchProvider>
        </LayoutProvider>
      </BrazeProvider>
    </ContentWrapper>
  );
};
Content.defaultProps = {
  layout: {},
  currentPortfolioName: '',
  isAuthenticated: false,
  currentUrl: '',
  hasSnackbarNotification: false,
};
export default Content;
